<template>
    <Teleport to="body">
        <Transition name="modal-fade">
            <div
                v-if="isVisible"
                class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-[2px]"
                @click.self="closeModal"
            >
                <div
                    class="w-full max-w-md md:max-w-lg bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="change-password-title"
                >
                    <div class="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                        <div class="flex-1 pr-4">
                            <h2 id="change-password-title" class="text-lg font-semibold text-gray-900">
                                Сменить пароль
                            </h2>
                            <p class="text-sm text-gray-500 mt-1">
                                Введите текущий пароль, затем укажите новый и повторите его.
                            </p>
                        </div>

                        <button
                            type="button"
                            class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            aria-label="Закрыть"
                            @click="closeModal"
                        >
                            <svg class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                        </button>
                    </div>

                    <div class="px-6 py-5 space-y-4">
                        <div class="rounded-lg bg-gray-50 border border-gray-100 px-4 py-3">
                            <p class="text-xs font-medium text-gray-700 mb-2">Требования к паролю</p>
                            <ul class="space-y-1 text-xs">
                                <li :class="isPasswordHasCapital && isPasswordHasLowerCase ? 'text-emerald-600' : 'text-gray-500'">
                                    Прописные и строчные буквы латинского алфавита (A-Z, a-z)
                                </li>
                                <li :class="isPasswordHasDigits ? 'text-emerald-600' : 'text-gray-500'">
                                    Хотя бы одна цифра (0-9)
                                </li>
                                <li :class="isPasswordHasSpecialChars ? 'text-emerald-600' : 'text-gray-500'">
                                    Хотя бы один спецсимвол (!@#$% и др.)
                                </li>
                                <li :class="isPasswordLengthValid ? 'text-emerald-600' : 'text-gray-500'">
                                    Длина от 8 до 48 символов включительно
                                </li>
                            </ul>
                        </div>

                        <div>
                            <label for="old-password" class="block text-sm font-medium text-gray-700 mb-1.5">
                                Текущий пароль
                            </label>
                            <input
                                id="old-password"
                                v-model="oldPassword"
                                type="password"
                                autocomplete="current-password"
                                placeholder="Введите текущий пароль"
                                class="w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                            />
                        </div>

                        <div>
                            <label for="new-password" class="block text-sm font-medium text-gray-700 mb-1.5">
                                Новый пароль
                            </label>
                            <input
                                id="new-password"
                                v-model="newPassword"
                                type="password"
                                autocomplete="new-password"
                                placeholder="Введите новый пароль"
                                class="w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                            />
                        </div>

                        <div>
                            <label for="repeat-password" class="block text-sm font-medium text-gray-700 mb-1.5">
                                Повторите новый пароль
                            </label>
                            <input
                                id="repeat-password"
                                v-model="repeatNewPassword"
                                type="password"
                                autocomplete="new-password"
                                placeholder="Повторите новый пароль"
                                class="w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                            />
                        </div>
                    </div>

                    <div class="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
                        <button
                            type="button"
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            @click="closeModal"
                        >
                            Отмена
                        </button>

                        <button
                            type="button"
                            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                            @click="changePassword"
                        >
                            Сохранить
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { computed, defineEmits, defineModel, ref, watch } from 'vue';
import { createToaster } from "@meforma/vue-toaster";
import stringUtils from "@/service/stringUtils";
import { AuthApi as authApi } from "@/service/authApi";

const toaster = createToaster();
const emit = defineEmits(['password-changed']);
const isVisible = defineModel();

const oldPassword = ref("");
const newPassword = ref("");
const repeatNewPassword = ref("");

const isPasswordHasCapital = computed(() => stringUtils.hasCapital(newPassword.value));
const isPasswordHasLowerCase = computed(() => stringUtils.hasLowerCase(newPassword.value));
const isPasswordHasDigits = computed(() => stringUtils.hasDigits(newPassword.value));
const isPasswordHasSpecialChars = computed(() => stringUtils.hasSpecialChars(newPassword.value));
const isPasswordLengthValid = computed(() => newPassword.value.length >= 8 && newPassword.value.length <= 32);

watch(isVisible, (visible) => {
    if (!visible) {
        resetForm();
    }
});

function resetForm() {
    oldPassword.value = "";
    newPassword.value = "";
    repeatNewPassword.value = "";
}

function closeModal() {
    isVisible.value = false;
}

async function changePassword() {
    if (oldPassword.value === "") {
        toaster.error("Введите текущий пароль");
        return;
    }

    if (newPassword.value === "" || repeatNewPassword.value === "") {
        toaster.error("Введите новый пароль");
        return;
    }

    if (newPassword.value !== repeatNewPassword.value) {
        toaster.error("Пароли не совпадают");
        return;
    }

    const response = await authApi.changePassword(oldPassword.value, newPassword.value);

    if (response?.status === "error") {
        toaster.error(response.message);
        return;
    }

    toaster.success("Пароль успешно изменён");
    emit('password-changed');
    closeModal();
}
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}
</style>
